// @file Surface post filter store
import { getVuexStore } from '@@/bits/pinia'
import { POST_COLOR_STRINGS } from '@@/bits/post_color'
import { constructFiltersFromQueryParams, getOrderedFilterState } from '@@/bits/surface_filter_state_manager'
import { isRegistered } from '@@/bits/user_model'
import { unboundedWatch, unboundedWatchEffect } from '@@/bits/vue'
import { SurfaceFilter } from 'enums'
import { cloneDeep, intersectionBy } from 'lodash-es'
import { postFilterState } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceCurrentUserStore } from 'pinia/surface_current_user'
import { useSurfaceUserContributorsStore } from 'pinia/surface_user_contributors'
import type { Post, User } from 'types'
import type {
  FilterGroup,
  FilterKeyForGroup,
  NonNullPostColor,
  PartialPostFilters,
  PostAuthorId,
  PostFilters,
  PostStatus,
} from 'types/surface'
import { computed, ref } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

function areSomeFiltersSelected(filters: PostFilters): boolean {
  return Object.values(filters).some((filterGroup) => Object.values(filterGroup).some((isSelected) => isSelected))
}

/**
 * Returns a predicate function for the given filter key in the given group.
 * The predicate function is used to tell if a post matches the filter.
 */
function getFilterPredicateForGroup<G extends FilterGroup>(
  group: G,
  filterKey: FilterKeyForGroup<G>,
): (p: Post) => boolean {
  switch (group) {
    case 'post_status':
      return (p: Post) => (filterKey === 'published' ? p.published === true : p.published !== true)
    case 'post_color':
      return (p: Post) => filterKey === p.color || (filterKey === 'default' && p.color == null)
    case 'post_author':
      // User ID is a number but since we store `filterKey` as object key, it's a string.
      // That's why we convert `author.id` to string here.
      return (p: Post) => p.author_id?.toString() === filterKey
    default:
      // TypeScript shouldn't let `group` to be none of the above values.
      // But the `case switch` syntax doesn't understand that.
      return () => true
  }
}

/**
 * Returns posts that match any of the filters in the given group (OR logic).
 */
function filterPostsByGroup<G extends FilterGroup>(posts: Post[], filters: PostFilters, group: G): Post[] {
  const filterGroup = filters[group]
  const allFilters = Object.keys(filterGroup)
  const selectedFilters = allFilters.filter((key) => filterGroup[key])
  if (selectedFilters.length === 0) return posts
  const results: Post[] = []
  selectedFilters.forEach((key) => {
    results.push(...posts.filter((p) => getFilterPredicateForGroup(group, key as FilterKeyForGroup<typeof group>)(p)))
  })
  return results
}

/**
 * Returns posts that match all set of filters by groups (AND logic).
 */
export function filterPosts(posts: Post[], filters: PostFilters): Post[] {
  if (!areSomeFiltersSelected(filters)) return posts
  return intersectionBy(
    filterPostsByGroup(posts, filters, 'post_status'),
    filterPostsByGroup(posts, filters, 'post_color'),
    filterPostsByGroup(posts, filters, 'post_author'),
    // We use `cid` as the unique identifier for posts.
    'cid',
  )
}

function createFilterState({
  currentFilters,
  xAuthor,
  postAuthors,
}: {
  currentFilters: PartialPostFilters
  xAuthor: boolean
  postAuthors: User[]
}): PostFilters {
  const postColorOptions = POST_COLOR_STRINGS.reduce((acc, color) => {
    acc[color] = currentFilters.post_color?.[color] ?? false
    return acc
  }, {}) as Record<FilterKeyForGroup<'post_color'>, boolean>

  const postAuthorOptions: Record<FilterKeyForGroup<'post_author'>, boolean> = {}
  if (xAuthor) {
    const registeredPostAuthors = postAuthors.filter((user) => isRegistered(user))
    registeredPostAuthors.forEach((user) => {
      postAuthorOptions[user.id] = currentFilters.post_author?.[user.id] ?? false
    })
  }
  return {
    post_status: {
      published: currentFilters.post_status?.published ?? false,
      submitted: currentFilters.post_status?.submitted ?? false,
    },
    post_color: postColorOptions,
    post_author: postAuthorOptions,
  }
}

export const useSurfaceFilterStore = defineStore('surfaceFilter', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  // Legacy filter value from Vuex store. Won't use this in the new search & filter feature.
  const filter = computed<SurfaceFilter>(() => surfaceVuexStore?.getters['postFilter/filter'])

  // Values from other stores
  const { colorScheme, xAuthor } = storeToRefs(useSurfaceStore())
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const { postAuthors, postAuthorsById } = storeToRefs(useSurfaceUserContributorsStore())

  // State
  const xFilterModal = ref(false)
  const filters = ref<PostFilters>(
    createFilterState({
      currentFilters: {},
      xAuthor: xAuthor.value,
      postAuthors: postAuthors.value,
    }),
  )

  // Getters
  const hasSelectedFilters = computed(() => areSomeFiltersSelected(filters.value))
  const orderedFilterState = computed(() =>
    getOrderedFilterState({
      filters: filters.value,
      isDarkMode: colorScheme.value === 'dark',
      postAuthorsById: postAuthorsById.value,
      currentUserId: currentUser.value?.id,
    }),
  )

  // Actions
  function togglePostFilter(group: FilterGroup, key: PostStatus | NonNullPostColor | PostAuthorId): void {
    const filterGroup = filters.value[group]
    const filterOption = filterGroup[key] as boolean | undefined
    if (filterOption != null) {
      filterGroup[key] = !filterOption
    }
  }

  function updatePostFilter(
    group: FilterGroup,
    key: PostStatus | NonNullPostColor | PostAuthorId,
    value: boolean,
  ): void {
    const filterGroup = filters.value[group]
    const filterOption = filterGroup[key] as boolean | undefined
    if (filterOption != null) {
      filterGroup[key] = value
    }
  }

  function resetPostFilters(group?: FilterGroup): void {
    const groupsToReset: FilterGroup[] = group == null ? ['post_status', 'post_color', 'post_author'] : [group]
    const newFilters = cloneDeep(filters.value)
    groupsToReset.forEach((group) => {
      const filterGroup = newFilters[group]
      for (const key in filterGroup) {
        const filterOption = filterGroup[key] as boolean | undefined
        if (filterOption != null) {
          filterGroup[key] = false
        }
      }
    })
    filters.value = newFilters
  }

  function setPostFiltersFromQueryParams(params: URLSearchParams): void {
    const newFilters = constructFiltersFromQueryParams(params)
    filters.value = createFilterState({
      currentFilters: newFilters,
      xAuthor: xAuthor.value,
      postAuthors: postAuthors.value,
    })
  }

  // Watchers
  void unboundedWatchEffect(() => {
    filters.value = createFilterState({
      currentFilters: filters.value,
      xAuthor: xAuthor.value,
      postAuthors: postAuthors.value,
    })
  })

  void unboundedWatch(orderedFilterState, (newState) => {
    postMessage(postFilterState(newState))
  })

  return {
    // Legacy
    filter,

    // State
    xFilterModal,
    filters,

    // Getters
    hasSelectedFilters,
    orderedFilterState,

    // Actions
    togglePostFilter,
    updatePostFilter,
    resetPostFilters,
    setPostFiltersFromQueryParams,
  }
})
